import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../App";

const DocumentsPage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      toast.error('Erro ao carregar documentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download iniciado');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Erro ao descarregar documento');
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: doc.file_type || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      if (doc.file_type?.includes('pdf') || doc.file_type?.includes('image') || doc.name.endsWith('.pdf') || doc.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
        window.open(url, '_blank');
      } else {
        handleDownload(doc);
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Erro ao visualizar documento');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = { contract: 'Contrato', certificate: 'Certificado', declaration: 'Declaração', id_document: 'Documento ID', other: 'Outro' };
    return labels[category] || category;
  };

  const getEntityTypeLabel = (type) => {
    const labels = { client: 'Cliente', supplier: 'Fornecedor', employee: 'Colaborador' };
    return labels[type] || type;
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Tem a certeza que deseja eliminar o documento "${doc.name}"? Esta ação não pode ser revertida.`)) {
      return;
    }
    setDeletingId(doc.document_id);
    try {
      await api.delete(`/documents/${doc.document_id}`);
      toast.success('Documento eliminado com sucesso');
      fetchDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Erro ao eliminar documento');
    } finally {
      setDeletingId(null);
    }
  };

  const canDelete = user?.role === 'super_admin' || user?.role === 'tenant_admin';

  return (
    <div data-testid="documents-page" className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans']">Gestão Documental</h1>
        <p className="text-muted-foreground">Documentos associados a entidades</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>
      ) : documents.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-xl border">
          <svg className="w-12 h-12 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-muted-foreground">Nenhum documento carregado</p>
          <p className="text-sm text-muted-foreground mt-2">Os documentos são carregados através das páginas de detalhe das entidades</p>
        </div>
      ) : (
        <div className="rounded-xl border bg-card overflow-hidden">
          <table className="data-table">
            <thead>
              <tr><th>Nome</th><th>Categoria</th><th>Entidade</th><th>Validade</th><th>Tamanho</th><th>Ações</th></tr>
            </thead>
            <tbody>
              {documents.map((doc) => (
                <tr key={doc.document_id}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      {doc.name}
                    </div>
                  </td>
                  <td><span className="px-2 py-1 rounded-full text-xs bg-muted">{getCategoryLabel(doc.category)}</span></td>
                  <td><span className="text-sm">{getEntityTypeLabel(doc.entity_type)}</span></td>
                  <td>
                    {doc.valid_until ? (
                      <span className={`text-sm ${new Date(doc.valid_until) < new Date() ? 'text-red-500' : ''}`}>
                        {new Date(doc.valid_until).toLocaleDateString('pt-PT')}
                      </span>
                    ) : '-'}
                  </td>
                  <td className="text-sm text-muted-foreground">{doc.file_size ? `${(doc.file_size / 1024).toFixed(1)} KB` : '-'}</td>
                  <td>
                    <div className="flex gap-1">
                      <button onClick={() => handleView(doc)} className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg" title="Visualizar" data-testid={`view-doc-${doc.document_id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </button>
                      <button onClick={() => handleDownload(doc)} className="p-2 text-emerald-500 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-lg" title="Descarregar" data-testid={`download-doc-${doc.document_id}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      {canDelete && (
                        <button 
                          onClick={() => handleDelete(doc)} 
                          disabled={deletingId === doc.document_id}
                          className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg disabled:opacity-50" 
                          title="Eliminar" 
                          data-testid={`delete-doc-${doc.document_id}`}
                        >
                          {deletingId === doc.document_id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-500 border-t-transparent"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
